import { useState, useEffect, useCallback } from 'react'
import { ChevronDown, Filter, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { SwipeableLeadCard } from './SwipeableLeadCard'
import { useMobileResponsive } from '@/hooks/useMobileResponsive'
import { cn } from '@/lib/utils'
import type { LeadTableRow } from '@/services/mockLeads'

interface MobileLeadReviewProps {
  leads: LeadTableRow[]
  onApprove: (leadId: string) => void
  onReject: (leadId: string) => void
  onViewDetails: (leadId: string) => void
  onLoadMore?: () => void
  hasMore?: boolean
  loading?: boolean
}

interface FilterState {
  riskLevel: 'all' | 'high' | 'medium' | 'low'
  timeframe: 'all' | 'today' | 'week' | 'month'
  status: 'all' | 'pending' | 'approved' | 'rejected'
  search: string
}

const filterChips = [
  { id: 'all', label: 'All', key: 'riskLevel' as const, value: 'all' },
  { id: 'high-risk', label: 'High Risk', key: 'riskLevel' as const, value: 'high' },
  { id: 'today', label: 'Today', key: 'timeframe' as const, value: 'today' },
  { id: 'pending', label: 'Pending', key: 'status' as const, value: 'pending' }
]

export function MobileLeadReview({
  leads,
  onApprove,
  onReject,
  onViewDetails,
  onLoadMore,
  hasMore = false,
  loading = false
}: MobileLeadReviewProps) {
  const [filters, setFilters] = useState<FilterState>({
    riskLevel: 'all',
    timeframe: 'all',
    status: 'all',
    search: ''
  })
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const { isMobile } = useMobileResponsive()

  // Infinite scroll observer
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null)

  const observerCallback = useCallback((entries: IntersectionObserverEntry[]) => {
    const target = entries[0]
    if (target.isIntersecting && hasMore && !loading && onLoadMore) {
      onLoadMore()
    }
  }, [hasMore, loading, onLoadMore])

  useEffect(() => {
    if (!sentinel) return

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1
    })

    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [sentinel, observerCallback])

  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const clearFilters = () => {
    setFilters({
      riskLevel: 'all',
      timeframe: 'all',
      status: 'all',
      search: ''
    })
  }

  const filteredLeads = leads.filter(lead => {
    // Risk level filter
    if (filters.riskLevel !== 'all') {
      const riskLevel = lead.riskScore >= 80 ? 'high' : 
                       lead.riskScore >= 60 ? 'medium' : 'low'
      if (riskLevel !== filters.riskLevel) return false
    }

    // Status filter
    if (filters.status !== 'all') {
      if (lead.status !== filters.status) return false
    }

    // Timeframe filter
    if (filters.timeframe !== 'all') {
      const now = new Date()
      const leadDate = new Date(lead.timestamp)
      
      switch (filters.timeframe) {
        case 'today':
          if (leadDate.toDateString() !== now.toDateString()) return false
          break
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
          if (leadDate < weekAgo) return false
          break
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
          if (leadDate < monthAgo) return false
          break
      }
    }

    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase()
      const searchableText = `${lead.firstName} ${lead.lastName} ${lead.email} ${lead.phone}`.toLowerCase()
      if (!searchableText.includes(searchTerm)) return false
    }

    return true
  })

  const activeFiltersCount = Object.values(filters).filter(value => 
    value !== 'all' && value !== ''
  ).length

  if (!isMobile) {
    return null // Only render on mobile
  }

  return (
    <div className="w-full">
      {/* Mobile Header */}
      <div className="p-4 border-b space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">Lead Review</h1>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSearchOpen(true)}
            >
              <Search className="h-4 w-4" />
            </Button>
            
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Filter className="h-4 w-4" />
                  Filters
                  {activeFiltersCount > 0 && (
                    <Badge className="h-4 w-4 p-0 text-xs">
                      {activeFiltersCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Filter Leads</SheetTitle>
                </SheetHeader>
                
                <div className="space-y-6 mt-6">
                  {/* Risk Level */}
                  <div>
                    <h3 className="font-medium mb-3">Risk Level</h3>
                    <div className="space-y-2">
                      {[
                        { value: 'all', label: 'All Levels' },
                        { value: 'high', label: 'High Risk (80+)' },
                        { value: 'medium', label: 'Medium Risk (60-79)' },
                        { value: 'low', label: 'Low Risk (<60)' }
                      ].map(option => (
                        <Button
                          key={option.value}
                          variant={filters.riskLevel === option.value ? "default" : "ghost"}
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => updateFilter('riskLevel', option.value as any)}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Timeframe */}
                  <div>
                    <h3 className="font-medium mb-3">Timeframe</h3>
                    <div className="space-y-2">
                      {[
                        { value: 'all', label: 'All Time' },
                        { value: 'today', label: 'Today' },
                        { value: 'week', label: 'Last 7 Days' },
                        { value: 'month', label: 'Last 30 Days' }
                      ].map(option => (
                        <Button
                          key={option.value}
                          variant={filters.timeframe === option.value ? "default" : "ghost"}
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => updateFilter('timeframe', option.value as any)}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Status */}
                  <div>
                    <h3 className="font-medium mb-3">Status</h3>
                    <div className="space-y-2">
                      {[
                        { value: 'all', label: 'All Statuses' },
                        { value: 'pending', label: 'Pending Review' },
                        { value: 'approved', label: 'Approved' },
                        { value: 'rejected', label: 'Rejected' }
                      ].map(option => (
                        <Button
                          key={option.value}
                          variant={filters.status === option.value ? "default" : "ghost"}
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => updateFilter('status', option.value as any)}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Clear Filters */}
                  {activeFiltersCount > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={clearFilters}
                    >
                      Clear All Filters
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Filter Chips */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {filterChips.map(chip => {
            const isActive = filters[chip.key] === chip.value
            return (
              <Button
                key={chip.id}
                variant={isActive ? "default" : "outline"}
                size="sm"
                className="whitespace-nowrap"
                onClick={() => updateFilter(chip.key, chip.value as any)}
              >
                {chip.label}
              </Button>
            )
          })}
        </div>

        {/* Results Count */}
        <div className="text-sm text-muted-foreground">
          {filteredLeads.length} leads
          {activeFiltersCount > 0 && ' (filtered)'}
        </div>
      </div>

      {/* Search Modal */}
      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-background">
          <div className="flex items-center gap-3 p-4 border-b">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSearchOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
            <Input
              type="search"
              placeholder="Search leads..."
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              className="flex-1"
              autoFocus
            />
          </div>
          <div className="p-4">
            {filters.search && (
              <div className="space-y-3">
                {filteredLeads.slice(0, 10).map(lead => (
                  <SwipeableLeadCard
                    key={lead.id}
                    lead={lead}
                    onApprove={onApprove}
                    onReject={onReject}
                    onViewDetails={onViewDetails}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Lead List */}
      <div className="p-4 space-y-3">
        {filteredLeads.map(lead => (
          <SwipeableLeadCard
            key={lead.id}
            lead={lead}
            onApprove={onApprove}
            onReject={onReject}
            onViewDetails={onViewDetails}
          />
        ))}

        {/* Infinite Scroll Sentinel */}
        {hasMore && (
          <div 
            ref={setSentinel}
            className="h-20 flex items-center justify-center"
          >
            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="text-sm">Loading more leads...</span>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                Scroll to load more
              </div>
            )}
          </div>
        )}

        {filteredLeads.length === 0 && (
          <div className="text-center py-12">
            <div className="text-muted-foreground mb-2">No leads found</div>
            <div className="text-sm text-muted-foreground">
              Try adjusting your filters or search terms
            </div>
            {activeFiltersCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={clearFilters}
              >
                Clear Filters
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}